import { AxiosResponse } from 'axios';
import {
  Annotation,
  DocumentVM,
  Head,
  Holders,
  ViewStepVM,
} from 'civil-status/view-models/step-form';
import {
  ActCategory,
  AllegatiModelEventoDTO,
  ConsultazioneANSCResponse,
  DatiAnnotazione,
  ModelEventoDTO,
  SubjectAnpr,
} from 'shell/models/act-category-list';
import { HttpResponse } from 'shell/models/http-response';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { DateTime } from 'luxon';
import { getNameRegister } from 'civil-status/components/shared/list-register';
import { getNameTypeContent } from 'civil-status/constants/id-type-content';
import { titleMotherFather } from 'civil-status/constants/title-mother-father';

const transformerDocument = (value?: Array<AllegatiModelEventoDTO>): DocumentVM => {
  return {
    files: (value || []).map((itemAllegati) => ({
      name: itemAllegati.descrizione || '',
      idAllegato: itemAllegati.id || '',
      tipoAllegato: itemAllegati.id || '',
      raw: itemAllegati.hashAllegato || '',
    })) as unknown as Array<TypeFile>,
  };
};

const transformerHead = (values?: ModelEventoDTO): Head => {
  return {
    idAnsc: values?.idAnsc || '',
    municipality: `${values?.nomeComuneRegistrazione || ''}${
      values?.nomeComuneRegistrazione && values.siglaProvinciaRegistrazione ? ',' : ''
    } ${values?.siglaProvinciaRegistrazione || ''}`,
    dateFormation: values?.dataformazione
      ? `${DateTime.fromISO(values?.dataformazione).toFormat('dd/MM/yyyy')} ${values.ora || '00'}:${
          values.minuto || '00'
        }`
      : undefined,
    effectiveDate:
      values?.dataDecorrenza && values?.dataformazione !== values?.dataDecorrenza
        ? `${DateTime.fromISO(values?.dataDecorrenza).toFormat('dd/MM/yyyy')} ${
            values.oraDecorrenza || '00'
          }:${values.minutoDecorrenza || '00'}`
        : undefined,
    state: values?.stato || '',
    register: getNameRegister(values?.idTipoEvento),
    idTipoEvento: values?.idTipoEvento,
    typeContent: getNameTypeContent(values?.idtipocontenuto),
    idUC: values?.idUsecase || '',
    descriptionsUC: values?.descrizioneCasoUso || '',
  };
};

const transformerSubject = (item: SubjectAnpr, title: string): Holders => {
  return {
    title,
    name: item.nome || '',
    surname: item.cognome || '',
    fiscalCode: item.codiceFiscale || '',
    idNationalIdentifier: item.idANPR || '',
    idAnpr: item.idsoggettoanpr || '',
    idAnsc: item.id || '',
    dateBorn: item?.dataNascita ? DateTime.fromISO(item?.dataNascita) : undefined,
    municipalityAire: item.nomeComuneAIRE
      ? {
          description: item.nomeComuneAIRE,
          province: item.siglaProvinciaAIRE,
          id: item.idComuneAIRE,
        }
      : undefined,
    municipality: item.nomeComuneNascita
      ? {
          description: item.nomeComuneNascita,
          province: item.siglaProvinciaNascita,
          id: item.idComuneNascita,
        }
      : undefined,
    state: item.idStatoNascita
      ? {
          id: item.idStatoNascita,
          value: item.nomeStatoNascita,
        }
      : undefined,
    idComuneResidenza: item.idComuneAIRE || item.idComuneResidenza,
  } as Holders;
};

const transformerHolders = (intestatari?: Array<SubjectAnpr>): Array<Holders> => {
  return (intestatari || [])?.map((item, index) => {
    return transformerSubject(item, `Intestatario ${index + 1}`);
  }) as Array<Holders>;
};

const transformerAnnotations = (values?: Array<DatiAnnotazione>): Array<Annotation> => {
  if ((values || []).length === 0) {
    return [];
  }

  return (values || [])?.map((item) => ({
    type: item.tipoAnnotazione || '',
    text: item.testoAnnotazione || '',
  }));
};

export const transformerActAnsc = (
  response: AxiosResponse<HttpResponse<ActCategory>>
): ViewStepVM => {
  const castedDatiAggiuntiviSide = response.data?.payload?.datiAggiuntiviSide || {};

  const castedResponseEvento =
    response.data?.payload?.consultazioneANSCResponse?.modelEventoDTO || [];
  const castedResponseSoggetto =
    (response.data?.payload as ConsultazioneANSCResponse)?.modelEventoDTO || [];

  const castedResponse = castedResponseEvento.length
    ? castedResponseEvento
    : castedResponseSoggetto;

  const castedResponseToTransform = castedResponse[0] || {};

  const head = transformerHead(castedResponseToTransform);
  const holders =
    transformerHolders(
      castedDatiAggiuntiviSide.intestatariTranscod || castedResponseToTransform.intestatari
    ) || [];
  const document = transformerDocument(castedResponseToTransform?.allegati);
  const annotations = transformerAnnotations(castedResponseToTransform.datiAnnotazione);
  const textAct = castedResponseToTransform?.composizioneCompleta || '';

  if (castedResponseToTransform.madre) {
    holders.push(
      transformerSubject(
        castedDatiAggiuntiviSide.madreTranscod || castedResponseToTransform.madre,
        titleMotherFather.Madre
      )
    );
  }

  if (castedResponseToTransform.padre) {
    holders.push(
      transformerSubject(
        castedDatiAggiuntiviSide.padreTranscod || castedResponseToTransform.padre,
        titleMotherFather.Padre
      )
    );
  }

  return {
    idAnsc: castedResponse[0]?.idAnsc || undefined,
    idEvento: castedDatiAggiuntiviSide.idEvento,
    idEventoCorrelato: castedDatiAggiuntiviSide.statoCivile?.idEventoCorrelato,
    document,
    holders,
    textAct,
    head,
    annotations,
  };
};
